import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import "./Branch.css";

const url = process.env.REACT_APP_BACKEND_URL;

const Branch = () => {
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    name: "",
    district: "",
    department: "",
  });
  const [districts, setDistricts] = useState([]);
  const [departments, setDepartments] = useState([]);

  const navigate = useNavigate();

  // Fetch all districts on mount
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await axios.get(`${url}/api/district`);
        setDistricts(response.data.data || []);
      } catch (err) {
        toast.error("Failed to load districts");
      }
    };
    fetchDistricts();
  }, []);

  // Fetch departments when district changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!data.district) {
        setDepartments([]);
        return;
      }
      try {
        const response = await axios.get(`${url}/api/department?district=${data.district}`);
        setDepartments(response.data.data || []);
      } catch (err) {
        toast.error("Failed to load departments for this district");
      }
    };
    fetchDepartments();
  }, [data.district]);

  const onChangeHandler = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === "district" ? { department: "" } : {}),
    }));
  };

  const onSubmitHandler = async (event) => {
    event.preventDefault();

    if (!data.name || !data.district || !data.department) {
      toast.error("All fields are required");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${url}/api/branch`, data);
      if (response.data.success) {
        toast.success("Branch added successfully!");
        setData({ name: "", district: "", department: "" });
        navigate("/ViewBranch");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to add Branch";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="district-container">
      <div className="header">
        <h2>Add New Branch</h2>
        <button onClick={() => navigate("/ViewBranch")} className="add-button">
          <Plus size={16} /> View Branches
        </button>
      </div>

      <form className="district-form" onSubmit={onSubmitHandler}>
        <div className="form-group">
          <label>Branch Name</label>
          <input
            type="text"
            name="name"
            value={data.name}
            onChange={onChangeHandler}
            required
          />
        </div>

        <div className="form-group">
          <label>District</label>
          <select
            name="district"
            value={data.district}
            onChange={onChangeHandler}
            required
          >
            <option value="">Select District</option>
            {districts.map((dist) => (
              <option key={dist._id} value={dist._id}>
                {dist.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Department</label>
          <select
            name="department"
            value={data.department}
            onChange={onChangeHandler}
            required
            disabled={!data.district}
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept._id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? "Processing..." : "Add Branch"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/ViewBranch")}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default Branch;